import { Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ISkillProject } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import { OPCheckBox } from '../../../OPcomponents/OPCheckBox'
import { OPDateField } from '../../../OPcomponents/OPDateField'
import { OPMultiline } from '../../../OPcomponents/OPMultiline'
import { OPNumberField } from '../../../OPcomponents/OPNumberField'
import { OPRadioButton } from '../../../OPcomponents/OPRadioButton'
import { OPSkillsCombobox } from '../../../OPcomponents/OPSkillsCombobox'
import { OPTextField } from '../../../OPcomponents/OPTextField'
import ProjectSkillLink from '../newpubproject/ProjectSkillLink'
import { createMandaysOnChangeHandler } from '../workHistoryHelpers'

import { initValues } from './AddNewEmployerJobTitlesProject'

interface NewPersonalPrivateProjectProps {
  setProjectSkills: (skills: ISkillProject[]) => void
  projectSkills: ISkillProject[]
  deleteProjectConfirmation?: boolean
  isNewProject?: boolean
  isEdit: boolean
}

const NewPersonalEmployerJobTitlesProject = (props: NewPersonalPrivateProjectProps): JSX.Element => {
  const { setProjectSkills, projectSkills, deleteProjectConfirmation, isNewProject } = props

  const { t } = useTranslation()
  const rootStore = useStore()
  const { dropdownRoles, getAllRoles, dropdownBranches, getAllBranches } = rootStore.projectStore
  const { validatedUser } = rootStore.loginStore

  useEffect(() => {
    getAllRoles()
  }, [getAllRoles])

  useEffect(() => {
    getAllBranches()
  }, [getAllBranches])

  const radioOptions = [
    {
      value: '1',
      label: t('Projects.ManDays'),
    },
    {
      value: '0',
      label: t('Projects.ManMonths'),
    },
  ]

  const { values, handleChange, setFieldValue, setValues, errors, touched, setTouched } = useFormikContext<initValues>()

  return (
    <Grid container gap={4}>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <OPTextField
            label={`${t('Projects.Employer')}*`}
            name='employer.name'
            value={values.employer.name ?? ''}
            onChange={(e) => {
              setTouched({ ...touched, employer: { ...touched.employer, name: true } })
              handleChange(e)
              // Always force creation of a new employer when adding
              if (isNewProject) {
                setFieldValue('employer.employerId', -1, false)
                setFieldValue('project.employerId', -1, false)
                setFieldValue('employer.userId', validatedUser, false)
              }
            }}
            fullWidth
            maxLength={200}
            disabled={deleteProjectConfirmation}
            error={touched.employer?.name && Boolean(errors.employer?.name)}
            errorText={t(errors.employer?.name || '')}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <OPSkillsCombobox
            multiple={false}
            name='project.branchId'
            label={`${t('Projects.EmployerBranch')}*`}
            onChange={(e, v) => {
              setTouched({ ...touched, project: { ...touched.project, branchId: true } })
              setFieldValue('project.branchId', v.key)
            }}
            disabled={deleteProjectConfirmation}
            optionList={dropdownBranches}
            error={touched.project?.branchId && Boolean(errors.project?.branchId)}
            helperText={t(errors.project?.branchId || '')}
            curState={dropdownBranches.find((b) => b.key === values.project.branchId) ?? null}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <OPTextField
            label={`${t('Projects.JobTitle')}*`}
            value={values.employer.jobTitle}
            name='employer.jobTitle'
            onChange={(e) => {
              setTouched({ ...touched, employer: { ...touched.employer, jobTitle: true } })
              handleChange(e)
            }}
            fullWidth
            maxLength={100}
            error={touched.employer?.jobTitle && Boolean(errors.employer?.jobTitle)}
            errorText={t(errors.employer?.jobTitle || '')}
            disabled={deleteProjectConfirmation}
          />
        </Grid>
        <Grid item md={3} sm={12}>
          <OPDateField
            disabled={deleteProjectConfirmation}
            label={`${t('Projects.EmployerStartDate')}*`}
            name='employer.startDate'
            value={values.employer.startDate}
            error={touched.employer?.startDate && Boolean(errors.employer?.startDate)}
            errMessage={t(errors.employer?.startDate || '')}
            onChange={(newValue: Date | null) => {
              setTouched({ ...touched, employer: { ...touched.employer, startDate: true } })
              setFieldValue('employer.startDate', newValue)
            }}
          />
        </Grid>
        <Grid item md={3} sm={12}>
          <OPDateField
            disabled={deleteProjectConfirmation}
            label={t('Projects.EmployerEndDate')}
            name='employer.endDate'
            error={Boolean(errors.employer?.endDate)}
            errMessage={t(errors.employer?.endDate || '')}
            minDate={values.employer.startDate}
            value={values.employer.endDate}
            onChange={(newValue: Date | null) => {
              setFieldValue('employer.endDate', newValue)
            }}
          />
        </Grid>
        <Grid xs={12} item>
          <OPMultiline
            name='employer.jobDescriptionFi'
            label={t('Projects.DescriptionOfMyWorkFi')}
            onChange={handleChange}
            maxChars={1000}
            placeholder={t('Projects.DescriptionOfMyWorkFi')}
            minRows={6}
            value={values.employer.jobDescriptionFi}
            disabled={deleteProjectConfirmation}
            hasCounter
          />
        </Grid>
        <Grid xs={12} item>
          <OPMultiline
            name='employer.jobDescriptionEng'
            label={t('Projects.DescriptionOfMyWorkEng')}
            onChange={handleChange}
            maxChars={1000}
            placeholder={t('Projects.DescriptionOfMyWorkEng')}
            minRows={6}
            value={values.employer.jobDescriptionEng}
            disabled={deleteProjectConfirmation}
            hasCounter
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container alignItems='center' spacing={2}>
            <Grid item xs={3}>
              {values.userProject.usingManDays ? (
                <OPNumberField
                  label={`${t('Projects.ProjectWorkAmount')}${
                    values.userProject.roleIds.length > 0 || projectSkills.length > 0 ? '*' : ''
                  }`}
                  name='userProject.manDays'
                  value={values.userProject.manDays}
                  error={Boolean(errors.userProject?.manDays)}
                  errorText={t(errors.userProject?.manDays || '')}
                  disabled={deleteProjectConfirmation}
                  onChange={(e) => setFieldValue('userProject.manDays', e ?? 0)}
                />
              ) : (
                <OPNumberField
                  label={`${t('Projects.ProjectWorkAmount')}${
                    values.userProject.roleIds.length > 0 || projectSkills.length > 0 ? '*' : ''
                  }`}
                  name='userProject.manMonths'
                  value={values.userProject.manMonths}
                  error={Boolean(errors.userProject?.manMonths)}
                  errorText={t(errors.userProject?.manMonths || '')}
                  disabled={deleteProjectConfirmation}
                  onChange={(e) => setFieldValue('userProject.manMonths', e ?? 0)}
                />
              )}
            </Grid>
            <Grid item className='griditem--formitem__radiomargin'>
              <OPRadioButton
                direction='row'
                name='userProject.usingManDays'
                radioOptions={radioOptions}
                label={''}
                disabled={deleteProjectConfirmation}
                onChange={createMandaysOnChangeHandler(values.userProject, (updated) => setValues({ ...values, userProject: updated }))}
                defaultVal={values.userProject.usingManDays ? '1' : '0'}
              />
            </Grid>
            <Grid item>
              <OPCheckBox
                name='userProject.isKeyProject'
                label={t('Projects.KeyEmployment')}
                value={values.userProject.isKeyProject ?? false}
                disabled={deleteProjectConfirmation}
                onChangeState={(_, checked) => setFieldValue('userProject.isKeyProject', checked)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item sm={12} md={6}>
          <OPSkillsCombobox
            name='userProject.roleIds'
            label={t('Projects.EmployersJobTitlesRoles')}
            placeholder={t('Placeholders.Choose')}
            multiple
            disabled={deleteProjectConfirmation}
            onDelete={(v) =>
              setFieldValue(
                'userProject.roleIds',
                values.userProject.roleIds.filter((role) => role.key !== v.option.key),
              )
            }
            optionList={dropdownRoles}
            groupBy={(opt: { categoryName?: string }) => opt.categoryName || t('Skills.Category')}
            onChange={(e, v) => setFieldValue('userProject.roleIds', v)}
            curState={values.userProject.roleIds}
          />
        </Grid>
        <Grid xs={12} item>
          <ProjectSkillLink
            isNewProject={isNewProject}
            projectId={values.userProject.projectId}
            deleteProjectConfirmation={deleteProjectConfirmation}
            setProjectSkills={setProjectSkills}
            curProjectSkills={projectSkills}
            isEmployerJobTitlesProject={true}
            setProjectFieldValue={setFieldValue}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default observer(NewPersonalEmployerJobTitlesProject)
